import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, MapPin, Activity, Building2, ChevronRight } from 'lucide-react';
import { api } from '../../services/api';
import { Team } from '../../types';

interface TeamWithStats extends Team {
  stats: {
    userCount: number;
    posCount: number;
    visitCount: number;
  };
}

export const AdminTeams: React.FC = () => {
  const [teams, setTeams] = useState<TeamWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('active');

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setIsLoading(true);
        const params = filter === 'all' ? {} : { is_active: filter === 'active' };
        const response = await api.get('/admin/teams', { params });
        setTeams(response.data.teams);
      } catch (err) {
        console.error('Failed to fetch teams:', err);
        setError('Failed to load teams');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeams();
  }, [filter]);

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      case 'growth': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadge = (team: Team) => {
    if (!team.is_active) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Inactive</span>;
    }
    if (team.subscription_status === 'trial') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Trial</span>;
    }
    if (team.subscription_status === 'past_due') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">Past Due</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-sm text-gray-500 mt-1">Manage all teams and their configurations</p>
        </div>
        <Link
          to="/admin/teams/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          <span>New Team</span>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {(['active', 'inactive', 'all'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Teams List */}
      {teams.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No teams found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {filter === 'all' ? 'Get started by creating a new team.' : `No ${filter} teams found.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {teams.map((team) => (
            <Link
              key={team.id}
              to={`/admin/teams/${team.id}`}
              className="block bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-semibold text-gray-900 truncate">
                        {team.name}
                      </h2>
                      {getStatusBadge(team)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPlanBadgeColor(team.plan)}`}>
                        {team.plan}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      {team.slug} • {team.billing_email || 'No billing email'}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                </div>

                {/* Stats */}
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users size={16} className="text-gray-400" />
                    <span>{team.stats?.userCount || 0} / {team.max_users} users</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin size={16} className="text-gray-400" />
                    <span>{team.stats?.posCount || 0} / {team.max_pos} PoS</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Activity size={16} className="text-gray-400" />
                    <span>{team.stats?.visitCount || 0} visits</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTeams;
